import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, map } from 'rxjs';

import { HEALTH_CHECKERS, HealthIssueDetails } from '@ideals/services/health-checker';
import { downloadFile } from '@ideals/utils';

export type TroubleshootingStatus = 'idle' | 'running' | 'failed' | 'success';

export interface TroubleshootingLogsProvider {
  getLogs: () => Promise<unknown[]>;
}

export const TROUBLESHOOTING_LOGS_PROVIDER = new InjectionToken<TroubleshootingLogsProvider>('TroubleshootingLogsProvider');

@Injectable({ providedIn: 'root' })
export class TroubleshootingService {
  readonly #checkers = inject(HEALTH_CHECKERS);
  readonly #logsProvider = inject(TROUBLESHOOTING_LOGS_PROVIDER);
  readonly #translateService = inject(TranslateService);

  readonly issues = signal<HealthIssueDetails[]>([]);
  readonly status = signal<TroubleshootingStatus>('idle');

  async downloadReport(): Promise<void> {
    const report = {
      ...this.#checkers.reduce((acc, checker) => ({
        ...acc,
        ...checker.getReport(),
      }), {}),
      logs: await this.#logsProvider.getLogs(),
    };

    const space = 2;
    const blob = new Blob([JSON.stringify(report, null, space)], { type: 'application/json' });
    const name = this.#translateService.instant('help_modal.TEXT.Troubleshooting') as string;
    const date = new Date().toISOString();

    downloadFile(blob, `${name}\u2014${date}.json`);
  }

  reset(): void {
    this.issues.set([]);
    this.status.set('idle');
  }

  runDiagnostic(): void {
    this.status.set('running');

    const results$ = this.#checkers.map((checker) => checker.hasIssue().pipe(
      map((hasIssue) => hasIssue ? checker.getIssueDetails() : null),
    ));

    forkJoin(results$).subscribe((results) => {
      const issues = results.filter(Boolean);
      const generalIssue: HealthIssueDetails = {
        title: 'help_modal.TEXT.Check_failed',
        description: 'help_modal.TEXT.We_ran_a_quick_check',
      };

      this.status.set(issues.length > 0 ? 'failed' : 'success');
      this.issues.set(issues.length > 1 ? [generalIssue, ...issues] : issues);
    });
  }
}
